// Pure MapLibre style helper — derives the PMTiles "basemap host"
// directory from the archive URL and substitutes `{basemapHost}`
// placeholders in a style JSON.
//
// Keeps `src/adapters/maplibre.ts` free of string-munging logic: the
// adapter passes the consumer's PMTiles source + the style object (or
// the bundled `protomaps-light.json`) through this helper, and hands
// the result to `new maplibregl.Map({ style })`.
//
// No `maplibre-gl`, no `pmtiles`, no browser APIs. The whole module
// runs under Vitest as plain TypeScript.
//
// See `FuseSimple_v1_Decisions.md` §5 (Bundled Default Style — Sprites
// and Glyphs) for why the placeholder pattern exists and why the
// consumer — not FuseSimple — is responsible for hosting the resolved
// sprite / glyph assets.

/**
 * Minimal style shape the resolver cares about. Anything else on the
 * style object is preserved structurally via the generic — we only
 * touch `sprite` and `glyphs`, never walk into `layers` / `sources`.
 *
 * Declared here rather than imported from `maplibre-gl` on purpose:
 * the helper must stay pure so `src/adapters/maplibre.ts` is the only
 * file that takes a dependency on MapLibre types.
 */
export interface StyleWithPlaceholders {
  // Deliberately `unknown` rather than `string` even though we only rewrite
  // the string form — MapLibre 5.x also allows `sprite` to be an array of
  // `{id,url}` entries for multi-sprite styles. Accept any shape and let
  // `resolveStylePlaceholders` decide at runtime.
  sprite?: unknown;
  glyphs?: unknown;
  [key: string]: unknown;
}

/**
 * Derive the basemap host (directory of the PMTiles source) from a
 * PMTiles URL or path.
 *
 * Rules:
 * - Strip `?query` and `#hash` before looking for the last `/`.
 *   Query strings on the `.pmtiles` fetch (auth tokens, cache busters)
 *   don't apply to sprite / glyph paths alongside it.
 * - The last `/` is the directory boundary. Everything before it is
 *   the host; everything after it is the filename.
 * - A bare filename with no `/` (e.g. `"kc.pmtiles"`) returns `""`,
 *   meaning "resolve placeholders against the current origin".
 *
 * Examples:
 * - `"https://cdn.example.com/king_county.pmtiles"`
 *     → `"https://cdn.example.com"`
 * - `"https://cdn.example.com/tiles/kc.pmtiles?v=3"`
 *     → `"https://cdn.example.com/tiles"`
 * - `"./tiles/kc.pmtiles"` → `"./tiles"`
 * - `"kc.pmtiles"` → `""`
 */
export function deriveBasemapHost(pmtilesSource: string): string {
  const withoutQuery = pmtilesSource.split("?")[0] ?? "";
  const withoutHash = withoutQuery.split("#")[0] ?? "";
  const lastSlash = withoutHash.lastIndexOf("/");
  if (lastSlash === -1) return "";
  return withoutHash.slice(0, lastSlash);
}

/**
 * Substitute every `{basemapHost}` occurrence in the style's `sprite`
 * and `glyphs` fields with the derived host. Returns a shallow copy —
 * the caller's style object is never mutated.
 *
 * Fields that aren't strings (or aren't present) pass through
 * untouched. The generic parameter is preserved so callers keep their
 * original type after substitution.
 *
 * v1 only rewrites `sprite` and `glyphs`. If a future style schema
 * adds another placeholder consumer (e.g. terrain / sky / projection
 * URLs), extend this function and add a test covering the new field.
 */
export function resolveStylePlaceholders<T extends StyleWithPlaceholders>(
  style: T,
  basemapHost: string,
): T {
  const next: T = { ...style };
  if (typeof next.sprite === "string") {
    next.sprite = next.sprite.replace(/\{basemapHost\}/g, basemapHost) as T["sprite"];
  }
  if (typeof next.glyphs === "string") {
    next.glyphs = next.glyphs.replace(/\{basemapHost\}/g, basemapHost) as T["glyphs"];
  }
  return next;
}
